import { useEffect, useRef, useState } from 'react';
import { Group, MessageField, createStyles } from '@ingeniorforeningen/figurine-core';
import { Dropzone as ManDropzone } from '@mantine/dropzone';
import FileList from './FileList';
import { useFileHandler } from '../../../hooks';
import { AppendDocument, DropzoneErrorCodes, Errors } from '../../../types';
import { FileErrorAlert } from '../../../helpers/fileHelper';
import '../PrintStyling.css';

const MaxFileSize = runtimeConfig.maxFileSize * 1024 ** 2; // mit.ida use this.
const MaxFileLimit = runtimeConfig.maxFileLimit;

type Props = {
  placeholder: string;
  fileUploadErrorTexts: Errors;
  handlePostMessage: (message: string, appendDocuments: AppendDocument[]) => void;
  postingCaseState: { isPosting: boolean; error: string | null; isPosted: boolean };
};

const styles = createStyles((theme, { dropZoneDisabled }: { dropZoneDisabled: boolean }) => ({
  root: {
    backgroundColor: theme.other.colorTokens.inputs.dark,
    borderWidth: 0,
    borderRadius: theme.radius.md,
    overflow: 'hidden',
    '&[data-accept]': {
      borderWidth: 2,
      borderColor: !dropZoneDisabled
        ? theme.other.colorTokens.backgrounds.primary
        : theme.other.colorTokens.feedback.error,
      backgroundColor: theme.other.colorTokens.inputs.dark,
    },
    '&[data-reject]': {
      borderWidth: 2,
      borderColor: theme.other.colorTokens.feedback.error,
    },
  },
}));

const CreateMessage = ({
  placeholder,
  fileUploadErrorTexts,
  handlePostMessage,
  postingCaseState,
}: Props) => {
  const openRef = useRef<() => void>(null);
  const [messageText, setMessageText] = useState<string>('');
  const [appendDocuments, setAppendDocuments] = useState<AppendDocument[]>([]);
  const dropZoneDisabled = MaxFileLimit - appendDocuments.length <= 0;
  const { classes } = styles({ dropZoneDisabled });

  const { handleOnFileAccepted, handleOnFileRejected, removeFile, droppedFiles, clearAllFiles } =
    useFileHandler(fileUploadErrorTexts);

  useEffect(() => {
    if (postingCaseState.isPosted) {
      setMessageText('');
      clearAllFiles();
      setAppendDocuments([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postingCaseState]);

  const handleAppendDocument = (documentId: string, fileName: string) => {
    setAppendDocuments((prevs) => [...prevs, { documentId, fileName }]);
  };

  const handleRemoveDocumentId = (documentId: string) => {
    setAppendDocuments((prevs) => prevs.filter((x) => x.documentId !== documentId));
  };

  const onPostMessage = () => {
    handlePostMessage(messageText, appendDocuments);
  };

  return (
    <Group direction="column" spacing={0} className="hidden-in-print">
      <ManDropzone
        openRef={openRef}
        activateOnClick={false}
        styles={{
          inner: { pointerEvents: 'all' },
        }}
        multiple={true}
        onDrop={(files) => {
          if (dropZoneDisabled) {
            files.forEach((file) => {
              FileErrorAlert(file.name, DropzoneErrorCodes.TOO_MANY_FILES, fileUploadErrorTexts);
            });
            return;
          }
          handleOnFileAccepted(files);
        }}
        onReject={(files) => {
          handleOnFileRejected(files);
        }}
        maxFiles={MaxFileLimit - appendDocuments.length}
        maxSize={MaxFileSize}
        p={0}
        classNames={classes}
        accept={{
          'application/pdf': ['.pdf'],
          'image/png': ['.png'],
          'image/jpg': ['.jpg'],
          'image/jpeg': ['.jpeg'],
          'image/gif': ['.gif'],
          'image/svg+xml': ['.svg'],
          'application/msword': ['.doc'],
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
          'application/vnd.ms-excel': ['.xls'],
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
          'application/vnd.ms-powerpoint': ['.ppt'],
          'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
          'text/csv': ['.csv'],
          'message/rfc822': ['.eml'],
          'application/vnd.ms-outlook': ['.msg'],
        }}
        data-cy="dropzone"
      >
        <Group grow direction="column" spacing={0}>
          <MessageField
            cypress="messageInput"
            m={0}
            name=""
            label=""
            placeholder={placeholder}
            onSend={onPostMessage}
            onAttach={() => {
              if (openRef && openRef.current) {
                openRef.current();
              }
            }}
            value={!postingCaseState.isPosting ? messageText : ''}
            onChange={(e) => {
              setMessageText(e.target.value);
            }}
            disableSendButton={
              (!messageText && appendDocuments.length <= 0) || postingCaseState.isPosting
            }
          />
          <div style={{ display: !postingCaseState.isPosting ? 'contents' : 'none' }}>
            <FileList
              droppedFiles={droppedFiles}
              removeFile={removeFile}
              errorTexts={fileUploadErrorTexts}
              setAppendDocument={handleAppendDocument}
              removeFileUploadId={handleRemoveDocumentId}
            />
          </div>
        </Group>
      </ManDropzone>
    </Group>
  );
};

export default CreateMessage;
